import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import Modal from '../ui/Modal'
import ProgressBar from '../ui/ProgressBar'
import { StatusBadge } from '../ui/StatusBadge'
import {
  Plus, ChevronDown, ChevronRight, Target, Layers, Zap, CheckSquare,
  Pencil, Trash2, Calendar, User, DollarSign, AlertTriangle, Check
} from 'lucide-react'

const STATUS_OPTIONS = [
  { value: 'pending',     label: 'Pendiente',   color: 'bg-gray-100 text-gray-600' },
  { value: 'in_progress', label: 'En progreso',  color: 'bg-blue-100 text-blue-700' },
  { value: 'at_risk',     label: 'En riesgo',    color: 'bg-amber-100 text-amber-700' },
  { value: 'completed',   label: 'Completado',   color: 'bg-green-100 text-green-700' },
]

const TASK_STATUS_OPTIONS = [
  { value: 'pending',     label: 'Pendiente',   color: 'bg-gray-100 text-gray-600' },
  { value: 'in_progress', label: 'En progreso',  color: 'bg-blue-100 text-blue-700' },
  { value: 'completed',   label: 'Completado',   color: 'bg-green-100 text-green-700' },
  { value: 'cancelled',   label: 'Cancelado',    color: 'bg-red-100 text-red-500' },
]

function StatusPill({ status, options = STATUS_OPTIONS }) {
  const opt = options.find(o => o.value === status) || options[0]
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>
      {opt.label}
    </span>
  )
}

// ── Task Row ───────────────────────────────────────────────────────────────
function TaskRow({ task, isAdmin, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ ...task })
  const [saving, setSaving] = useState(false)

  async function save() {
    setSaving(true)
    await supabase.from('activity_tasks').update({
      name: form.name, description: form.description,
      responsible: form.responsible, due_date: form.due_date || null,
      status: form.status,
    }).eq('id', task.id)
    onUpdate({ ...task, ...form })
    setEditing(false)
    setSaving(false)
  }

  async function toggleStatus() {
    const next = task.status === 'completed' ? 'in_progress' : 'completed'
    await supabase.from('activity_tasks').update({ status: next }).eq('id', task.id)
    onUpdate({ ...task, status: next })
  }

  return (
    <div className={`flex items-start gap-3 px-3 py-2 rounded-lg border transition-all group ${
      task.status === 'completed' ? 'bg-green-50 border-green-100' : 'bg-white border-gray-100 hover:border-gray-200'
    }`}>
      {/* Checkbox */}
      <button onClick={toggleStatus} className="mt-0.5 flex-shrink-0">
        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
          task.status === 'completed'
            ? 'bg-green-500 border-green-500 text-white'
            : 'border-gray-300 hover:border-brand-400'
        }`}>
          {task.status === 'completed' && <Check size={10} strokeWidth={3} />}
        </div>
      </button>

      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="space-y-2">
            <input className="input text-xs py-1" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <div className="grid grid-cols-3 gap-2">
              <input className="input text-xs py-1" placeholder="Responsable"
                value={form.responsible || ''} onChange={e => setForm(f => ({ ...f, responsible: e.target.value }))} />
              <input type="date" className="input text-xs py-1"
                value={form.due_date || ''} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
              <select className="input text-xs py-1" value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {TASK_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={save} disabled={saving} className="btn-primary text-xs py-1 px-3">
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
              <button onClick={() => setEditing(false)} className="btn-secondary text-xs py-1 px-3">Cancelar</button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-medium ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-800'}`}>
              {task.name}
            </span>
            {task.responsible && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <User size={10} /> {task.responsible}
              </span>
            )}
            {task.due_date && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Calendar size={10} /> {new Date(task.due_date).toLocaleDateString('es-CO', { day:'2-digit', month:'short' })}
              </span>
            )}
            <StatusPill status={task.status} options={TASK_STATUS_OPTIONS} />
          </div>
        )}
      </div>

      {isAdmin && !editing && (
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => setEditing(true)} className="p-1 text-gray-300 hover:text-brand-500">
            <Pencil size={12} />
          </button>
          <button onClick={() => onDelete(task.id)} className="p-1 text-gray-300 hover:text-red-500">
            <Trash2 size={12} />
          </button>
        </div>
      )}
    </div>
  )
}

// ── Activity Card ──────────────────────────────────────────────────────────
function ActivityCard({ activity, isAdmin, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false)
  const [tasks, setTasks] = useState([])
  const [loadingTasks, setLoadingTasks] = useState(false)
  const [addingTask, setAddingTask] = useState(false)
  const [newTask, setNewTask] = useState({ name: '', responsible: '', due_date: '' })
  const [savingTask, setSavingTask] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [form, setForm] = useState({ ...activity })
  const [saving, setSaving] = useState(false)

  async function loadTasks() {
    setLoadingTasks(true)
    const { data } = await supabase.from('activity_tasks')
      .select('*').eq('activity_id', activity.id).order('task_order')
    setTasks(data || [])
    setLoadingTasks(false)
  }

  async function toggleExpand() {
    if (!expanded && tasks.length === 0) await loadTasks()
    setExpanded(e => !e)
  }

  async function addTask(e) {
    e.preventDefault()
    if (!newTask.name.trim()) return
    setSavingTask(true)
    const { data } = await supabase.from('activity_tasks').insert({
      activity_id: activity.id,
      consultancy_id: activity.consultancy_id,
      name: newTask.name,
      responsible: newTask.responsible || null,
      due_date: newTask.due_date || null,
      task_order: tasks.length,
    }).select().single()
    if (data) setTasks(prev => [...prev, data])
    setNewTask({ name: '', responsible: '', due_date: '' })
    setAddingTask(false)
    setSavingTask(false)
  }

  async function updateTask(updated) {
    setTasks(prev => prev.map(t => t.id === updated.id ? updated : t))
    // Recalculate activity progress
    const allTasks = tasks.map(t => t.id === updated.id ? updated : t)
    const done = allTasks.filter(t => t.status === 'completed').length
    const pct = allTasks.length ? Math.round((done / allTasks.length) * 100) : 0
    await supabase.from('strategic_activities').update({ progress: pct }).eq('id', activity.id)
    onUpdate({ ...activity, progress: pct })
  }

  async function deleteTask(taskId) {
    if (!confirm('¿Eliminar esta tarea?')) return
    await supabase.from('activity_tasks').delete().eq('id', taskId)
    setTasks(prev => prev.filter(t => t.id !== taskId))
  }

  async function saveEdit(e) {
    e.preventDefault()
    setSaving(true)
    await supabase.from('strategic_activities').update({
      name: form.name, description: form.description,
      responsible: form.responsible, budget: form.budget,
      start_date: form.start_date || null, due_date: form.due_date || null,
      status: form.status,
    }).eq('id', activity.id)
    onUpdate({ ...activity, ...form })
    setEditOpen(false)
    setSaving(false)
  }

  const completedTasks = tasks.filter(t => t.status === 'completed').length

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* Activity header */}
      <div className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
        activity.status === 'completed' ? 'bg-green-50' : 'bg-white'
      }`} onClick={toggleExpand}>
        <div className="w-6 h-6 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
          <Zap size={12} className="text-purple-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-800">{activity.name}</span>
            <StatusPill status={activity.status} />
            {activity.responsible && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <User size={10} /> {activity.responsible}
              </span>
            )}
            {activity.due_date && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Calendar size={10} /> {new Date(activity.due_date).toLocaleDateString('es-CO', { day:'2-digit', month:'short', year:'numeric' })}
              </span>
            )}
          </div>
          {expanded && tasks.length > 0 && (
            <p className="text-xs text-gray-400 mt-0.5">{completedTasks}/{tasks.length} tareas · {activity.progress ?? 0}%</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button onClick={e => { e.stopPropagation(); setEditOpen(true) }}
              className="p-1.5 text-gray-300 hover:text-brand-500 rounded hover:bg-brand-50">
              <Pencil size={13} />
            </button>
          )}
          {isAdmin && (
            <button onClick={e => { e.stopPropagation(); onDelete(activity.id) }}
              className="p-1.5 text-gray-300 hover:text-red-500 rounded hover:bg-red-50">
              <Trash2 size={13} />
            </button>
          )}
          {expanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
        </div>
      </div>

      {/* Tasks */}
      {expanded && (
        <div className="px-4 py-3 bg-gray-50 space-y-2 border-t border-gray-100">
          {loadingTasks && <p className="text-xs text-gray-400 text-center py-2">Cargando tareas...</p>}
          {tasks.map(t => (
            <TaskRow key={t.id} task={t} isAdmin={isAdmin} onUpdate={updateTask} onDelete={deleteTask} />
          ))}
          {tasks.length === 0 && !loadingTasks && (
            <p className="text-xs text-gray-400 text-center py-2">Sin tareas aún</p>
          )}
          {isAdmin && (
            addingTask ? (
              <form onSubmit={addTask} className="flex items-center gap-2 pt-1">
                <input className="input text-xs py-1.5 flex-1" placeholder="Nombre de la tarea *"
                  value={newTask.name} onChange={e => setNewTask(f => ({ ...f, name: e.target.value }))} autoFocus required />
                <input className="input text-xs py-1.5 w-32" placeholder="Responsable"
                  value={newTask.responsible} onChange={e => setNewTask(f => ({ ...f, responsible: e.target.value }))} />
                <input type="date" className="input text-xs py-1.5 w-32"
                  value={newTask.due_date} onChange={e => setNewTask(f => ({ ...f, due_date: e.target.value }))} />
                <button type="submit" disabled={savingTask} className="btn-primary text-xs py-1.5 px-3 whitespace-nowrap">
                  {savingTask ? '...' : 'Agregar'}
                </button>
                <button type="button" onClick={() => setAddingTask(false)} className="btn-secondary text-xs py-1.5 px-2">✕</button>
              </form>
            ) : (
              <button onClick={() => setAddingTask(true)}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-brand-600 py-1 px-2 hover:bg-white rounded-lg transition-colors w-full">
                <Plus size={12} /> Agregar tarea
              </button>
            )
          )}
        </div>
      )}

      {/* Edit modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Editar Actividad Estratégica">
        <form onSubmit={saveEdit} className="space-y-3">
          <div>
            <label className="label">Nombre *</label>
            <input className="input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={2} value={form.description || ''}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Responsable</label>
              <input className="input" value={form.responsible || ''} onChange={e => setForm(f => ({ ...f, responsible: e.target.value }))} />
            </div>
            <div>
              <label className="label">Presupuesto</label>
              <input className="input" placeholder="ej. USD 50.000" value={form.budget || ''} onChange={e => setForm(f => ({ ...f, budget: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Inicio</label>
              <input type="date" className="input" value={form.start_date || ''} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div>
              <label className="label">Fecha límite</label>
              <input type="date" className="input" value={form.due_date || ''} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div>
              <label className="label">Estado</label>
              <select className="input" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setEditOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

// ── Pillar Card ────────────────────────────────────────────────────────────
function PillarCard({ pillar, isAdmin, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false)
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [actForm, setActForm] = useState({ name: '', description: '', responsible: '', budget: '', start_date: '', due_date: '', status: 'pending' })
  const [savingAct, setSavingAct] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [form, setForm] = useState({ ...pillar })
  const [saving, setSaving] = useState(false)

  async function loadActivities() {
    setLoading(true)
    const { data } = await supabase.from('strategic_activities')
      .select('*').eq('pillar_id', pillar.id).order('activity_order')
    setActivities(data || [])
    setLoading(false)
  }

  async function toggleExpand() {
    if (!expanded && activities.length === 0) await loadActivities()
    setExpanded(e => !e)
  }

  async function addActivity(e) {
    e.preventDefault()
    setSavingAct(true)
    const { data } = await supabase.from('strategic_activities').insert({
      pillar_id: pillar.id,
      consultancy_id: pillar.consultancy_id,
      name: actForm.name,
      description: actForm.description || null,
      responsible: actForm.responsible || null,
      budget: actForm.budget || null,
      start_date: actForm.start_date || null,
      due_date: actForm.due_date || null,
      status: actForm.status,
      activity_order: activities.length,
    }).select().single()
    if (data) setActivities(prev => [...prev, data])
    setActForm({ name: '', description: '', responsible: '', budget: '', start_date: '', due_date: '', status: 'pending' })
    setAddOpen(false)
    setSavingAct(false)
  }

  async function updateActivity(updated) {
    setActivities(prev => prev.map(a => a.id === updated.id ? updated : a))
  }

  async function deleteActivity(actId) {
    if (!confirm('¿Eliminar esta actividad y sus tareas?')) return
    await supabase.from('strategic_activities').delete().eq('id', actId)
    setActivities(prev => prev.filter(a => a.id !== actId))
  }

  async function saveEdit(e) {
    e.preventDefault()
    setSaving(true)
    await supabase.from('strategic_pillars').update({
      name: form.name, description: form.description, status: form.status
    }).eq('id', pillar.id)
    onUpdate({ ...pillar, ...form })
    setEditOpen(false)
    setSaving(false)
  }

  const doneActivities = activities.filter(a => a.status === 'completed').length

  return (
    <div className="border border-blue-100 rounded-xl overflow-hidden">
      {/* Pillar header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 cursor-pointer hover:bg-blue-100/60 transition-colors"
        onClick={toggleExpand}>
        <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
          <Layers size={14} className="text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-blue-900">{pillar.name}</span>
            <StatusPill status={pillar.status} />
          </div>
          {expanded && activities.length > 0 && (
            <p className="text-xs text-blue-500 mt-0.5">{doneActivities}/{activities.length} actividades completadas</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <button onClick={e => { e.stopPropagation(); setEditOpen(true) }}
                className="p-1.5 text-blue-300 hover:text-blue-600 rounded transition-colors">
                <Pencil size={13} />
              </button>
              <button onClick={e => { e.stopPropagation(); onDelete(pillar.id) }}
                className="p-1.5 text-blue-300 hover:text-red-500 rounded transition-colors">
                <Trash2 size={13} />
              </button>
            </>
          )}
          {expanded ? <ChevronDown size={14} className="text-blue-400" /> : <ChevronRight size={14} className="text-blue-400" />}
        </div>
      </div>

      {/* Activities */}
      {expanded && (
        <div className="px-4 py-3 space-y-2">
          {loading && <p className="text-xs text-gray-400 text-center py-2">Cargando actividades...</p>}
          {activities.map(a => (
            <ActivityCard key={a.id} activity={a} isAdmin={isAdmin} onUpdate={updateActivity} onDelete={deleteActivity} />
          ))}
          {activities.length === 0 && !loading && (
            <p className="text-xs text-gray-400 text-center py-3">Sin actividades estratégicas aún</p>
          )}
          {isAdmin && (
            <button onClick={() => setAddOpen(true)}
              className="flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-700 py-1.5 px-2 hover:bg-blue-50 rounded-lg transition-colors w-full mt-1">
              <Plus size={12} /> Agregar actividad estratégica
            </button>
          )}
        </div>
      )}

      {/* Add activity modal */}
      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Nueva Actividad Estratégica">
        <form onSubmit={addActivity} className="space-y-3">
          <div>
            <label className="label">Nombre de la actividad *</label>
            <input className="input" required value={actForm.name}
              onChange={e => setActForm(f => ({ ...f, name: e.target.value }))}
              placeholder="ej. Lanzamiento de campaña digital de marca" />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={2} value={actForm.description}
              onChange={e => setActForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Responsable</label>
              <input className="input" placeholder="Nombre o área" value={actForm.responsible}
                onChange={e => setActForm(f => ({ ...f, responsible: e.target.value }))} />
            </div>
            <div>
              <label className="label">Presupuesto</label>
              <input className="input" placeholder="ej. USD 20.000" value={actForm.budget}
                onChange={e => setActForm(f => ({ ...f, budget: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">Inicio</label>
              <input type="date" className="input" value={actForm.start_date}
                onChange={e => setActForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div>
              <label className="label">Fecha límite</label>
              <input type="date" className="input" value={actForm.due_date}
                onChange={e => setActForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div>
              <label className="label">Estado</label>
              <select className="input" value={actForm.status}
                onChange={e => setActForm(f => ({ ...f, status: e.target.value }))}>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setAddOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingAct} className="btn-primary flex-1">
              {savingAct ? 'Creando...' : 'Crear Actividad'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit pillar modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Editar Pilar Estratégico">
        <form onSubmit={saveEdit} className="space-y-3">
          <div>
            <label className="label">Nombre *</label>
            <input className="input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={2} value={form.description || ''}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div>
            <label className="label">Estado</label>
            <select className="input" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setEditOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

// ── Objective Card ─────────────────────────────────────────────────────────
function ObjectiveCard({ objective, isAdmin, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(true)
  const [pillars, setPillars] = useState([])
  const [loading, setLoading] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [pillarForm, setPillarForm] = useState({ name: '', description: '', status: 'pending' })
  const [savingPillar, setSavingPillar] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [form, setForm] = useState({ ...objective })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadPillars()
  }, [])

  async function loadPillars() {
    setLoading(true)
    const { data } = await supabase.from('strategic_pillars')
      .select('*').eq('objective_id', objective.id).order('pillar_order')
    setPillars(data || [])
    setLoading(false)
  }

  async function addPillar(e) {
    e.preventDefault()
    setSavingPillar(true)
    const { data } = await supabase.from('strategic_pillars').insert({
      objective_id: objective.id,
      consultancy_id: objective.consultancy_id,
      name: pillarForm.name,
      description: pillarForm.description || null,
      status: pillarForm.status,
      pillar_order: pillars.length,
    }).select().single()
    if (data) setPillars(prev => [...prev, data])
    setPillarForm({ name: '', description: '', status: 'pending' })
    setAddOpen(false)
    setSavingPillar(false)
  }

  async function deletePillar(pillarId) {
    if (!confirm('¿Eliminar este pilar y todas sus actividades?')) return
    await supabase.from('strategic_pillars').delete().eq('id', pillarId)
    setPillars(prev => prev.filter(p => p.id !== pillarId))
  }

  async function saveEdit(e) {
    e.preventDefault()
    setSaving(true)
    await supabase.from('strategic_objectives').update({
      name: form.name, description: form.description,
      target_date: form.target_date || null, status: form.status,
    }).eq('id', objective.id)
    onUpdate({ ...objective, ...form })
    setEditOpen(false)
    setSaving(false)
  }

  const donePillars = pillars.filter(p => p.status === 'completed').length

  return (
    <div className="card border-brand-200 overflow-hidden">
      {/* Objective header */}
      <div className="flex items-start gap-4 mb-3">
        <div className="w-10 h-10 rounded-xl bg-brand-700 flex items-center justify-center flex-shrink-0 text-white">
          <Target size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-bold text-gray-900 text-sm leading-tight">{objective.name}</h3>
              {objective.description && <p className="text-xs text-gray-500 mt-0.5">{objective.description}</p>}
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                <StatusPill status={objective.status} />
                {objective.target_date && (
                  <span className="flex items-center gap-1 text-xs text-gray-400">
                    <Calendar size={10} /> Meta: {new Date(objective.target_date).toLocaleDateString('es-CO', { day:'2-digit', month:'short', year:'numeric' })}
                  </span>
                )}
                <span className="text-xs text-gray-400">{donePillars}/{pillars.length} pilares</span>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {isAdmin && (
                <>
                  <button onClick={() => setEditOpen(true)}
                    className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => onDelete(objective.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 size={14} />
                  </button>
                </>
              )}
              <button onClick={() => setExpanded(e => !e)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors">
                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <ProgressBar value={objective.progress ?? 0} size="sm" showLabel={false} />
      <p className="text-xs text-gray-400 mt-1 mb-3">{objective.progress ?? 0}% completado</p>

      {/* Pillars */}
      {expanded && (
        <div className="space-y-2 border-t border-gray-100 pt-3">
          {loading && <p className="text-xs text-gray-400 text-center py-2">Cargando pilares...</p>}
          {pillars.map(p => (
            <PillarCard key={p.id} pillar={p} isAdmin={isAdmin}
              onUpdate={updated => setPillars(prev => prev.map(x => x.id === updated.id ? updated : x))}
              onDelete={deletePillar} />
          ))}
          {pillars.length === 0 && !loading && (
            <p className="text-xs text-gray-400 text-center py-3">Sin pilares estratégicos aún</p>
          )}
          {isAdmin && (
            <button onClick={() => setAddOpen(true)}
              className="flex items-center gap-2 text-xs text-brand-600 hover:text-brand-800 py-2 px-3 hover:bg-brand-50 rounded-xl transition-colors w-full mt-1 border border-dashed border-brand-200 hover:border-brand-400">
              <Plus size={13} /> Agregar pilar estratégico
            </button>
          )}
        </div>
      )}

      {/* Add pillar modal */}
      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Nuevo Pilar Estratégico">
        <form onSubmit={addPillar} className="space-y-3">
          <div>
            <label className="label">Nombre del pilar *</label>
            <input className="input" required value={pillarForm.name}
              onChange={e => setPillarForm(f => ({ ...f, name: e.target.value }))}
              placeholder="ej. Transformación digital del canal" />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={2} value={pillarForm.description}
              onChange={e => setPillarForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setAddOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingPillar} className="btn-primary flex-1">
              {savingPillar ? 'Creando...' : 'Crear Pilar'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit objective modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Editar Objetivo Estratégico">
        <form onSubmit={saveEdit} className="space-y-3">
          <div>
            <label className="label">Nombre del objetivo *</label>
            <input className="input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={2} value={form.description || ''}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Fecha meta</label>
              <input type="date" className="input" value={form.target_date || ''}
                onChange={e => setForm(f => ({ ...f, target_date: e.target.value }))} />
            </div>
            <div>
              <label className="label">Estado</label>
              <select className="input" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setEditOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Guardando...' : 'Guardar'}</button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

// ── Main TacticalPlanView ──────────────────────────────────────────────────
export default function TacticalPlanView({ consultancyId }) {
  const { isAdmin } = useAuth()
  const [objectives, setObjectives] = useState([])
  const [loading, setLoading] = useState(true)
  const [addOpen, setAddOpen] = useState(false)
  const [objForm, setObjForm] = useState({ name: '', description: '', target_date: '', status: 'pending' })
  const [savingObj, setSavingObj] = useState(false)

  useEffect(() => {
    loadObjectives()
  }, [consultancyId])

  async function loadObjectives() {
    setLoading(true)
    const { data } = await supabase.from('strategic_objectives')
      .select('*').eq('consultancy_id', consultancyId).order('obj_order')
    setObjectives(data || [])
    setLoading(false)
  }

  async function addObjective(e) {
    e.preventDefault()
    setSavingObj(true)
    const { data } = await supabase.from('strategic_objectives').insert({
      consultancy_id: consultancyId,
      name: objForm.name,
      description: objForm.description || null,
      target_date: objForm.target_date || null,
      status: objForm.status,
      obj_order: objectives.length,
    }).select().single()
    if (data) setObjectives(prev => [...prev, data])
    setObjForm({ name: '', description: '', target_date: '', status: 'pending' })
    setAddOpen(false)
    setSavingObj(false)
  }

  async function deleteObjective(objId) {
    if (!confirm('¿Eliminar este objetivo y todos sus pilares y actividades?')) return
    await supabase.from('strategic_objectives').delete().eq('id', objId)
    setObjectives(prev => prev.filter(o => o.id !== objId))
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-4 border-brand-500 border-t-transparent" />
    </div>
  )

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-900">Plan Táctico</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Objetivos estratégicos → Pilares → Actividades → Tareas
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setAddOpen(true)} className="btn-primary flex items-center gap-2 text-sm">
            <Plus size={15} /> Nuevo Objetivo
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap">
        <span className="flex items-center gap-1.5"><Target size={12} className="text-brand-600" /> Objetivo estratégico</span>
        <span className="flex items-center gap-1.5"><Layers size={12} className="text-blue-500" /> Pilar</span>
        <span className="flex items-center gap-1.5"><Zap size={12} className="text-purple-500" /> Actividad estratégica</span>
        <span className="flex items-center gap-1.5"><CheckSquare size={12} className="text-gray-400" /> Tarea</span>
      </div>

      {/* Objectives */}
      {objectives.length === 0 ? (
        <div className="card text-center py-12">
          <Target size={40} className="mx-auto mb-3 text-gray-200" />
          <p className="text-gray-400 font-medium">Sin objetivos estratégicos aún</p>
          <p className="text-xs text-gray-400 mt-1 mb-4">
            Los objetivos nacen del Plan Estratégico. Agrega el primero para comenzar el Plan Táctico.
          </p>
          {isAdmin && (
            <button onClick={() => setAddOpen(true)} className="btn-primary mx-auto">
              <Plus size={14} /> Agregar primer objetivo
            </button>
          )}
        </div>
      ) : (
        objectives.map(obj => (
          <ObjectiveCard key={obj.id} objective={obj} isAdmin={isAdmin}
            onUpdate={updated => setObjectives(prev => prev.map(o => o.id === updated.id ? updated : o))}
            onDelete={deleteObjective} />
        ))
      )}

      {/* Add objective modal */}
      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Nuevo Objetivo Estratégico">
        <form onSubmit={addObjective} className="space-y-3">
          <div>
            <label className="label">Nombre del objetivo *</label>
            <input className="input" required value={objForm.name}
              onChange={e => setObjForm(f => ({ ...f, name: e.target.value }))}
              placeholder="ej. Crecer 20% de participación de mercado en 3 años" />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={2} value={objForm.description}
              onChange={e => setObjForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Contexto o métricas de éxito..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Fecha meta</label>
              <input type="date" className="input" value={objForm.target_date}
                onChange={e => setObjForm(f => ({ ...f, target_date: e.target.value }))} />
            </div>
            <div>
              <label className="label">Estado inicial</label>
              <select className="input" value={objForm.status}
                onChange={e => setObjForm(f => ({ ...f, status: e.target.value }))}>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setAddOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingObj} className="btn-primary flex-1">
              {savingObj ? 'Creando...' : 'Crear Objetivo'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
